import React, { FC, memo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Header } from "@/components/Header";
import { UserContext } from "@/components/User";
import { putContactPerson } from "@/data/assignments";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { Flex, IDialogModal, inDialogModal, Text } from "@/eworkitComponents";
import {
  ContactPersonFormik,
  IContactPersonFormValues,
} from "@/views/Assignments/Details/components/ContactPersonFormik";
import { CloseButton } from "@/views/components/CloseButton";
import { StyledModal } from "@/views/components/Modal";

interface IContactPersonDialogProps extends IDialogModal {
  assignmentId: number;
  consultantProfile: IConsultantProfile;
  onSave: (close: () => void) => void;
  onCancel?: () => void;
}

const ContactPersonDialog = inDialogModal(
  memo<IContactPersonDialogProps>(({ assignmentId, consultantProfile, onClose, onCancel, onSave }) => {
    const { t } = useTranslation("session");
    const { contextConsultantProfile } = useContext(UserContext);
    const companyId = contextConsultantProfile?.sharedProfile?.company.id;

    const handleCancel = useCallback(() => {
      onClose();
      onCancel?.();
    }, [onClose, onCancel]);

    const handleSave = useCallback(
      async (values: IContactPersonFormValues) => {
        await putContactPerson({ assignmentId, consultantProfileId: values.consultantProfile.id, companyId });

        await onSave(onClose);

        onClose();
      },
      [onSave, onClose],
    );

    return (
      <StyledModal>
        <Flex direction="column" padding={["lg"]} innerMargin="lg">
          <Flex justify="space-between">
            <Header weight="bold">{t("ASSIGNMENTS.DETAILS.CONTACT_PERSON.MODAL_HEADER")}</Header>
            <CloseButton onClick={handleCancel} testName="close" />
          </Flex>

          <Flex direction="column" innerMargin="sm">
            <Text color="secondary">{t("ASSIGNMENTS.DETAILS.CONTACT_PERSON.SELECT_LABEL")}</Text>

            <ContactPersonFormik
              initialValues={{ consultantProfile, companyId: companyId! }}
              onSubmit={handleSave}
              onCancel={handleCancel}
            />
          </Flex>
        </Flex>
      </StyledModal>
    );
  }),
);

export interface IChangeContactPersonDialog extends IContactPersonDialogProps {}

export const ContactPersonDialogModal: FC<IChangeContactPersonDialog> = memo(props => (
  <ContactPersonDialog size="xs" {...props} />
));
